import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { headerStyle } from "../block.style";
import { Edit, Print } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../Redux/hooks";
import { useMemo, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import http from "../../../../utils/http";
import BackButton from "../../../Common/UI/BackButton";

type Props = {
  consentEditable: boolean;
};

const CBPreviewHeader: React.FC<Props> = ({ consentEditable }) => {
  const consentName = useAppSelector((state) => state.consent.consentName);
  const type = useAppSelector((state) => state.consent.type);
  const versionId = useAppSelector((state) => state.consent.versionId);
  const formId = useAppSelector((state) => state.consent.formId);
  const studyId = useAppSelector((state) => state.consent.studyId);
  const formType = useAppSelector((state) => state.consent.formType);
  const consentStatus = useAppSelector((state) => state.consent.status);
  const blocks = useAppSelector((state) => state.consent.blocks);
  const editable = useMemo(
    () => consentStatus === "draft" && consentEditable,
    [consentStatus, consentEditable]
  );

  const [exportLoading, setExportLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`../edit?versionId=${versionId}&formType=${formType}`, {
      relative: "path",
    });
  };

  const exportXml = async () => {
    try {
      setExportLoading(true);
      //Check for type
      const prefix =
        type === "platform"
          ? `/platform/admin/econsent`
          : formType === "sunshine"
          ? `/admin/study/${studyId}/sunshine`
          : `/admin/study/${studyId}/econsent`;
      let httpUrl = `${prefix}/${formId}/versions/${versionId}/export`;
      const res = await http.get(httpUrl, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${consentName}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportLoading(false);
    } catch (err: any) {
      setExportLoading(false);
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Export failed");
      } catch (e) {
        errorToastMessage(err as Error);
      }
    }
  };

  const handlePrint = async () => {
    if (!blocks || blocks.length === 0) {
      errorToastMessage(
        new Error(
          "Failed to print PDF: Form is empty. Please add content before printing."
        )
      );
      return;
    }

    try {
      setPrintLoading(true);
      const prefix =
        type === "platform"
          ? `/platform/admin/econsent`
          : formType === "sunshine"
          ? `/admin/study/${studyId}/sunshine`
          : `/admin/study/${studyId}/econsent`;
      let httpUrl = `${prefix}/${formId}/versions/${versionId}/download-pdf`;
      const res = await http.get(httpUrl, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${consentName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toastMessage("success", "PDF downloaded successfully");
      setPrintLoading(false);
    } catch (err) {
      setPrintLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Box sx={headerStyle}>
      <BackButton onClick={handleBack} sx={{ mr: 1 }} />
      <Typography
        variant="h2"
        color="text.primary"
        sx={{
          flex: 1,
          minWidth: "0px",
        }}
        noWrap
      >
        {consentName || "Form Preview"}
      </Typography>
      <span style={{ marginLeft: "auto" }}></span>
      {formId && (
        <Button
          startIcon={
            printLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Print />
            )
          }
          onClick={handlePrint}
          sx={{ mr: 2 }}
          variant="outlined"
          disabled={printLoading || exportLoading}
        >
          Print
        </Button>
      )}
      {formId && (
        <Button
          startIcon={
            exportLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={exportXml}
          sx={{ mr: 2 }}
          variant="outlined"
          disabled={printLoading || exportLoading}
        >
          Export XML
        </Button>
      )}
      {editable && (
        <Button variant="contained" startIcon={<Edit />} onClick={handleEdit}>
          Edit
        </Button>
      )}
    </Box>
  );
};

export default CBPreviewHeader;
